import AbstractPerson from './AbstractPerson'
import { Publikation, PubIdent } from 'fobclient/lib/entity'
import publikationsTyp from '@/schema/PublikationsTyp'
import Mittelgeber from '@/schema/Mittelgeber'

import FieldsGeneric from './FieldsGeneric'
let Fields = FieldsGeneric('Publikation')

import { validIsoLangCodes } from '@/lang_codes'

export default {
  label: 'Publikation',
  route: 'publikation',
  className: 'publikation',
  class: Publikation,
  fields: [
    Fields.organisationseinheit,
    Fields.relevantFuerWeitere,
    Fields.weitereOrganisationseinheiten,
    publikationsTyp,
    {
      label: 'Publikation.form.veroeffentlichungsJahr',
      field: 'veroeffentlichungsJahr',
      type: Number,
      icon: 'calendar-o',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== ''
      }
    },
    {
      label: 'Publikation.form.titelPublikation',
      field: 'titelPublikation',
      maxlength: 500,
      type: 'string',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== ''
      }
    },
    {
      label: 'Publikation.form.author',
      singularLabel: 'Autor',
      field: 'author',
      render: 'stringRep',
      type: 'object',
      schema: AbstractPerson,
      finder: {
        name: 'byMultiValueSearchRep',
        multiValue: true,
        minLength: 3
      },
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          [
            'ARTIKEL', 'SAMMELBANDBEITRAG', 'TAGUNGSBEITRAG', 'MONOGRAPHIE',
            'QUELLENEDITION', 'BERICHT', 'WORKINGPAPER',
            'BEITRAGINNICHTWISSENSCHAFTLICHENMEDIEN',
            'DISSERTATION', 'HABILITATIONSSCHRIFT', 'SONSTIGE', 'BUCHBEITRAG'
          ].includes(data.publikationsTyp)
      },
      icon: 'user',
      cardinality: 'many'
    },
    {
      label: 'Publikation.form.herausgeber',
      field: 'herausgeber',
      render: 'stringRep',
      type: 'object',
      schema: AbstractPerson,
      finder: {
        name: 'byMultiValueSearchRep',
        multiValue: true,
        minLength: 3
      },
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          !['ARTIKEL', 'DISSERTATION', 'HABILITATIONSSCHRIFT']
          .includes(data.publikationsTyp)
      },
      icon: 'user',
      cardinality: 'many'
    },
    {
      label: 'Publikation.form.institution',
      field: 'institution',
      type: 'string',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          !['ARTIKEL', 'DISSERTATION', 'HABILITATIONSSCHRIFT']
          .includes(data.publikationsTyp)
      }
    },
    {
      label: 'Publikation.form.internalPubIdent',
      className: 'pubIdent',
      field: 'pubIdent',
      type: 'object',
      render: 'stringRep',
      onlyCreate: true,
      cardinality: 'many',
      inEditView: (_data, user) => {
        // inEditView must return a Boolean
        return user.roles.ADMIN === true
      },
      plain: true,
      editable: true,
      view (data, _user) {
        return data.publikationsTyp != null && data.publikationsTyp
      },
      schema: {
        label: 'PubIdent',
        route: 'pubident',
        entityName: 'de.ul.fob.domain.PubIdent',
        class: PubIdent,
        className: 'pubIdent',
        fields: [
          {
            ...Fields.PrefetchedSingleReference(
              'pubIdentTyp',
              'pubIdentTyp',
              'folder-open'),
            preselect: (typ) => typ.kennung === 'PUBMAN',
            prefilter: (typ) => typ.kennung === 'PUBMAN',
            label: 'Publikation.form.publicPubIdent.pubIdentTyp'
          },
          {
            ...Fields.String('wert'),
            label: 'Publikation.form.publicPubIdent.wert'
          }
        ]
      }
    },
    {
      label: 'Publikation.form.publicPubIdent',
      field: 'publicPubIdent',
      type: 'object',
      render: 'embedded',
      onlyCreate: true,
      cardinality: 'many',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== ''
      },
      schema: {
        route: 'pubident',
        className: 'pubIdent',
        entityName: 'de.ul.fob.domain.PubIdent',
        compactPrintView: true,
        fields: [
          {
            ...Fields.PrefetchedSingleReference(
              'pubIdentTyp',
              'pubIdentTyp',
              'folder-open'
            ),
            prefilter: (typ) => typ.kennung !== 'PUBMAN',
            orderBy: 'kennung',
            appendClass: ['compact-print-view-label']
          },
          {
            label: 'Publikation.form.publicPubIdent.wert',
            field: 'wert',
            appendClass: ['compact-print-view-value'],
            view (data) {
              return !(data.externalLink && data.externalLink.href)
            }
          },
          {
            field: 'externalLink',
            label: 'Publikation.form.publicPubIdent.wert',
            type: 'link',
            computed (data) {
              let pubIdentTyp = data.pubIdentTyp.kennung.toLowerCase()
              let hrefs = {
                urn: (id) => {
                  if (id.includes('urn:nbn')) {
                    // try to fix URNn that already contain a resolver or other random stuff
                    let parts = id.split('/')
                    let theURN = parts[parts.length - 1]
                    // second check to see if we actually got a URN
                    if (theURN.startsWith('urn:nbn')) {
                      return `https://nbn-resolving.org/${theURN}`
                    } else {
                      return null
                    }
                  } else {
                    return null
                  }
                },
                doi: (id) => `https://doi.org/${id}`,
                pmid: (id) => `https://www.ncbi.nlm.nih.gov/pubmed/${id}`,
                isbn: (id) => `https://www.worldcat.org/isbn/${id}`,
                issn: (id) => `https://katalog.ub.uni-leipzig.de/Search/Results?lookfor0[]=${id}&amp;type0[]=ISN`,
                ismn: (id) => `https://katalog.ub.uni-leipzig.de/Search/Results?lookfor0[]=${id}&amp;type0[]=ISN`,
                isi: (id) => `https://ws.isiknowledge.com/cps/openurl/service?url_ver=Z39.88-2004&rft_id=info:ut/${id}`,
                uri: (id) => id
              }

              let href = null

              if (hrefs[pubIdentTyp] && hrefs[pubIdentTyp](data.wert.trim())) {
                href = encodeURI(hrefs[pubIdentTyp](data.wert.trim()))
              }

              return { href, text: data.wert }
            },
            view (data) {
              return data.externalLink && data.externalLink.href
            }
          }
        ]
      }
    },
    {
      label: 'Publikation.form.journal',
      field: 'journal',
      type: 'string',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          [
            'ARTIKEL', 'BEITRAGINNICHTWISSENSCHAFTLICHENMEDIEN', 'SONDERHEFT'
          ].includes(data.publikationsTyp)
      }
    },
    {
      label: 'Publikation.form.buchtitel',
      field: 'buchtitel',
      type: 'string',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          [
            'SAMMELBANDBEITRAG', 'TAGUNGSBEITRAG', 'BEITRAGINNICHTWISSENSCHAFTLICHENMEDIEN'
          ].includes(data.publikationsTyp)
      }
    },
    {
      label: 'Publikation.form.serientitel',
      field: 'serientitel',
      type: 'string',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          ![
            'ARTIKEL', 'DISSERTATION', 'HABILITATIONSSCHRIFT', 'SONDERHEFT'
          ].includes(data.publikationsTyp)
      }
    },
    {
      ...Fields.SimpleMultiReference(
        'foerderer',
        'credit-card',
        Mittelgeber
      ),
      finder: {
        name: 'bySearchRepLike',
        multiValue: false,
        minLength: 0,
        opts: {
          sort: 'finSchluessel'
        }
      },
      allowCreate: false,
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== ''
      }
    },
    {
      label: 'Publikation.form.inProjekt',
      field: 'inProjekt',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== ''
      },
      type: 'select',
      translatable: true,
      values: [
        {
          value: true,
          label: 'boolean.yes'
        },
        {
          value: false,
          label: 'boolean.no'
        },
        {
          value: null,
          label: ''
        }
      ]
    },
    {
      label: 'Publikation.form.forschungsprojekt',
      field: 'forschungsprojekt',
      type: 'object',
      cardinality: 'single',
      schema: {
        route: 'forschungsprojekt',
        className: 'forschungsprojekt'
      },
      finder: {
        name: 'byMultiValueSearchRep',
        multiValue: true,
        minLength: 0
      },
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' && data.inProjekt === true
      }
    },
    {
      label: 'Publikation.form.verleger',
      field: 'verleger',
      type: 'string',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          !['DISSERTATION', 'HABILITATIONSSCHRIFT']
          .includes(data.publikationsTyp)
      }
    },
    {
      label: 'Publikation.form.verlagsort',
      field: 'verlagsort',
      type: 'string',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          !['DISSERTATION', 'HABILITATIONSSCHRIFT']
          .includes(data.publikationsTyp)
      }
    },
    {
      label: 'Publikation.form.band',
      field: 'band',
      type: 'string',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          ![
            'DISSERTATION', 'HABILITATIONSSCHRIFT'
          ].includes(data.publikationsTyp)
      }
    },
    {
      label: 'Publikation.form.journalNummer',
      field: 'journalNummer',
      type: 'string',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          [
            'ARTIKEL', 'BEITRAGINNICHTWISSENSCHAFTLICHENMEDIEN', 'SONDERHEFT'
          ].includes(data.publikationsTyp)
      }
    },
    {
      label: 'Publikation.form.seiteStart',
      field: 'seiteStart',
      type: 'string',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          [
            'ARTIKEL', 'BUCHBEITRAG', 'BEITRAGINNICHTWISSENSCHAFTLICHENMEDIEN', 'SAMMELBANDBEITRAG',
            'TAGUNGSBEITRAG', 'WORKINGPAPER', 'BERICHT', 'SONSTIGE'
          ].includes(data.publikationsTyp)
      }
    },
    {
      label: 'Publikation.form.seiteEnde',
      field: 'seiteEnde',
      type: 'string',
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== '' &&
          [
            'ARTIKEL', 'BUCHBEITRAG', 'BEITRAGINNICHTWISSENSCHAFTLICHENMEDIEN', 'SAMMELBANDBEITRAG',
            'TAGUNGSBEITRAG', 'WORKINGPAPER', 'BERICHT', 'SONSTIGE'
          ].includes(data.publikationsTyp)
      }
    },
    {
      label: 'Publikation.form.sprache',
      field: 'sprache',
      type: 'string',
      translatable: true,
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== ''
      },
      render (val) {
        let codes = val.split(',')
          .map(s => s.trim())
          .filter(s => s.length !== 0)
          .map(code => {
            if (validIsoLangCodes.has(code)) {
              return `iso639-2b::${code}`
            } else {
              return code
            }
          })
        return codes
      }
    },
    {
      label: 'Publikation.form.istFinal',
      field: 'istFinal',
      inPublicView: false,
      type: Boolean,
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== ''
      }
    },
    {
      label: 'Publikation.form.validiert',
      field: 'validiert',
      inPublicView: false,
      inEditView: false,
      type: Boolean,
      view (data, user) {
        return data.publikationsTyp != null && data.publikationsTyp !== ''
      },
      editable: false
    }
  ]
}



// WEBPACK FOOTER //
// ./src/schema/Publikation.js